import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE } from "../../../../api";
import { getSessionUserId, getSessionEmpresaId, isSessionSuperAdmin } from "../../../../utils/SessionUser";
import { useLogos } from "../../../../context/LogoProvider";
import { ENDPOINTS } from "../../Utils/Endpoints";
import sigmaDelete from '../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../Pages/img/Editar.png';
import ConfirmDeleteDialog from "../../../security/ConfirmDeleteDialog";
import AlertDialog from "../../../Lms/Components/Shared/AlertDialog";

const SERVER_URL = API_BASE.replace(/\/api\/?$/, '');
const UPLOAD_ENDPOINT = `${API_BASE}/upload/articulo`;

const getTodayISO = () => {
    return new Date().toISOString().slice(0, 10);
};

const Articulos = ({ onNavigate, initialItem }) => {
    const { getSystemConfig } = useLogos();
    const [articulos, setArticulos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [areasNegocio, setAreasNegocio] = useState([]);
    const [selectedId, setSelectedId] = useState(null);

    const [filtros, setFiltros] = useState({
        texto: "",
        categoria: "Todas",
        area: "Todas",
    });

    const [formData, setFormData] = useState(() => ({
        titulo: "",
        resumen: "",
        categoria: null,
        areaNegocio: null,
        contenido: "",
        autor: "",
        fechaPublicacion: getTodayISO(),
        imagen: null,
        destacado: false,
    }));

    const [errores, setErrores] = useState({});
    const [imagenNombre, setImagenNombre] = useState(null);

    // Estados para manejo de imágenes
    const [selectedFile, setSelectedFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [previousImagePath, setPreviousImagePath] = useState(null);
    const [uploading, setUploading] = useState(false);

    // Estados para el modal
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
    const [nombreAutor, setNombreAutor] = useState(""); // Nombre completo del autor
    const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
    const [alertDialog, setAlertDialog] = useState({ open: false, title: "", message: "", variant: "info" });

    // Estados para el modal de compartir
    const [showShareModal, setShowShareModal] = useState(false);
    const [descendantCompanies, setDescendantCompanies] = useState([]);
    const [selectedCompanies, setSelectedCompanies] = useState({});
    const [sharingLoading, setSharingLoading] = useState(false);
    // Payload temporal para no perder datos entre modales
    const [pendingPayload, setPendingPayload] = useState(null);
    const [pendingFinalImageUrl, setPendingFinalImageUrl] = useState(null);

    // Cargar articulos y categorías desde API
    useEffect(() => {
        const cargarDatos = async () => {
            try {
                // Obtener el ID del usuario de sesión
                const idUsuario = getSessionUserId();

                const resCategorias = await axios.get(ENDPOINTS.categorias);
                const cats = resCategorias.data;
                setCategorias(cats);

                // Cargar áreas de negocio
                const resAreas = await axios.get(ENDPOINTS.areasNegocios);
                const isSuperAdmin = isSessionSuperAdmin();
                const sessionEmpresaId = getSessionEmpresaId();
                
                // Filtrar según lógica de AreaNegocioCatalog: mi empresa o globales (idEmpresa null/0)
                const areas = (resAreas.data || []).filter(r => {
                    if (isSuperAdmin) return true;
                    const empId = r.idEmpresa || r.IdEmpresa;
                    return empId === sessionEmpresaId || !empId || empId === 0;
                });
                setAreasNegocio(areas);

                // Cargar usuarios y personas para obtener el nombre del autor
                const [resUsuarios, resPersonas] = await Promise.all([
                    axios.get(ENDPOINTS.usuarios),
                    axios.get(ENDPOINTS.personas),
                ]);

                const usuarios = resUsuarios.data || [];
                const personas = resPersonas.data || [];

                console.log('ID Usuario de sesión:', idUsuario);
                console.log('Usuarios:', usuarios);
                console.log('Personas:', personas);

                // Encontrar el usuario actual
                const usuarioActual = usuarios.find(u => u.idUsuario === idUsuario || u.IdUsuario === idUsuario);
                console.log('Usuario actual:', usuarioActual);

                if (usuarioActual) {
                    // Obtener idPersona (puede venir en camelCase o PascalCase)
                    const idPersona = usuarioActual.idPersona || usuarioActual.IdPersona;
                    console.log('ID Persona:', idPersona);

                    // Encontrar la persona asociada
                    const personaActual = personas.find(p => p.idPersona === idPersona || p.IdPersona === idPersona);
                    console.log('Persona actual:', personaActual);

                    if (personaActual) {
                        // Normalizar nombres (pueden venir en camelCase o PascalCase)
                        const nombre = personaActual.nombre || personaActual.Nombre || '';
                        const apellidoPaterno = personaActual.apellidoPaterno || personaActual.ApellidoPaterno || '';
                        const apellidoMaterno = personaActual.apellidoMaterno || personaActual.ApellidoMaterno || '';

                        const nombreCompleto = `${nombre} ${apellidoPaterno} ${apellidoMaterno}`.trim();
                        console.log('Nombre completo:', nombreCompleto);

                        setNombreAutor(nombreCompleto);
                        // Actualizar formData con el nombre del autor
                        setFormData(prev => ({
                            ...prev,
                            autor: nombreCompleto
                        }));
                    }
                }

                // Cargar artículos
                const response = await axios.get(ENDPOINTS.articulos);
                const data = response.data;

                // Crear mapeo de ID a nombre de área
                const areasMap = {};
                areas.forEach(a => {
                    areasMap[a.idAreaNegocio ?? a.IdAreaNegocio] = a.nombre ?? a.Nombre;
                });

                // Crear mapeo de ID a nombre de categoría
                const categoriasMap = {};
                cats.forEach(c => {
                    categoriasMap[c.idCategoria ?? c.IdCategoria] = c.nombre ?? c.Nombre;
                });

                // Crear mapeo de ID Usuario a Nombre Persona para Autores
                const autoresMap = {};
                usuarios.forEach(u => {
                    const uid = u.idUsuario ?? u.IdUsuario;
                    const pid = u.idPersona ?? u.IdPersona;
                    const persona = personas.find(p => (p.idPersona ?? p.IdPersona) === pid);
                    if (persona) {
                        const nombre = persona.nombre ?? persona.Nombre ?? '';
                        const apP = persona.apellidoPaterno ?? persona.ApellidoPaterno ?? '';
                        const apM = persona.apellidoMaterno ?? persona.ApellidoMaterno ?? '';
                        autoresMap[uid] = `${nombre} ${apP} ${apM}`.trim();
                    } else {
                        autoresMap[uid] = u.nombreUsuario ?? u.NombreUsuario ?? "Usuario System";
                    }
                });

                const transformados = data.map(a => ({
                    id: a.idArticulo ?? a.IdArticulo,
                    titulo: a.titulo ?? a.Titulo,
                    categoria: a.nombreCategoria ?? a.NombreCategoria ?? (categoriasMap[a.idCategoria ?? a.IdCategoria] || "General"),
                    idCategoria: a.idCategoria ?? a.IdCategoria,
                    area: a.nombreArea ?? a.NombreArea ?? (areasMap[a.idAreaNegocio ?? a.IdAreaNegocio] || "Corporativo"),
                    idAreaNegocio: a.idAreaNegocio ?? a.IdAreaNegocio,
                    contenido: a.contenido ?? a.Contenido,
                    autor: a.nombreAutor ?? a.NombreAutor ?? (autoresMap[a.idAutor ?? a.IdAutor] || "Desconocido"),
                    fechaPublicacion: (a.fechaPublicacion ?? a.FechaPublicacion) ? (a.fechaPublicacion ?? a.FechaPublicacion).slice(0, 10) : getTodayISO(),
                    imagen: (a.imagenUrl ?? a.ImagenUrl) || null,
                    resumen: (a.resumen ?? a.Resumen) || "",
                    destacado: a.destacado ?? a.Destacado ?? false,
                    esOrigen: a.esOrigen ?? a.EsOrigen ?? false
                }));
                // Ordenar: Destacados primero, luego por fecha de publicación descendente
                transformados.sort((a, b) => {
                    if (a.destacado && !b.destacado) return -1;
                    if (!a.destacado && b.destacado) return 1;
                    const dateA = new Date(a.fechaPublicacion || 0).getTime();
                    const dateB = new Date(b.fechaPublicacion || 0).getTime();
                    if (dateB !== dateA) return dateB - dateA;
                    return b.id - a.id; // Clave única para orden estable
                });

                setArticulos(transformados);

                // Selection Logic
                let articuloToSelect = null;
                if (initialItem) {
                    articuloToSelect = transformados.find(a => a.id === initialItem.id);
                }

                if (!articuloToSelect && transformados.length > 0) {
                    articuloToSelect = transformados[0];
                }

                if (articuloToSelect) {
                    setSelectedId(articuloToSelect.id);
                    setFormData({
                        titulo: articuloToSelect.titulo,
                        categoria: articuloToSelect.idCategoria,
                        areaNegocio: articuloToSelect.idAreaNegocio,
                        contenido: articuloToSelect.contenido,
                        autor: articuloToSelect.autor,
                        fechaPublicacion: articuloToSelect.fechaPublicacion,
                        imagen: articuloToSelect.imagen || null,
                    });
                    setImagePreview(articuloToSelect.imagen ? (articuloToSelect.imagen.startsWith('http') ? articuloToSelect.imagen : `${SERVER_URL}${articuloToSelect.imagen}`) : null);
                    setImagenNombre(articuloToSelect.imagen ? articuloToSelect.imagen.split('/').pop() : null);
                    setPreviousImagePath(articuloToSelect.imagen || null);
                } else {
                    // Si no hay artículos, setear defaults con la primera categoría y área
                    setFormData(prev => ({
                        ...prev,
                        categoria: cats.length > 0 ? (cats[0].idCategoria ?? cats[0].IdCategoria) : null,
                        areaNegocio: areas.length > 0 ? (areas[0].idAreaNegocio ?? areas[0].IdAreaNegocio) : null
                    }));
                }
            } catch (error) {
                console.error("Error al cargar datos:", error);
            }
        };
        cargarDatos();
    }, [initialItem]);

    const handleFiltrosChange = (e) => {
        const { name, value } = e.target;
        setFiltros((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setErrores((prev) => ({ ...prev, imagen: 'Solo se permiten archivos de imagen.' }));
            return;
        }

        const maxMB = parseFloat(getSystemConfig('LIMITE_PESO_IMAGENES', '5'));
        if (file.size > maxMB * 1024 * 1024) {
            setErrores((prev) => ({ ...prev, imagen: `La imagen no debe superar los ${maxMB}MB.` }));
            return;
        }

        setSelectedFile(file);
        const blobUrl = URL.createObjectURL(file);
        setImagePreview(blobUrl);
        setImagenNombre(file.name);
        setErrores((prev) => ({ ...prev, imagen: '' }));
    };

    const limpiarFormulario = () => {
        setSelectedId(null);
        setFormData({
            titulo: "",
            resumen: "",
            categoria: (categorias.length > 0 ? (categorias[0].idCategoria ?? categorias[0].IdCategoria) : null),
            areaNegocio: (areasNegocio.length > 0 ? (areasNegocio[0].idAreaNegocio ?? areasNegocio[0].IdAreaNegocio) : null),
            contenido: "",
            autor: nombreAutor, // Auto-llenar con el nombre del autor
            fechaPublicacion: getTodayISO(),
            imagen: null,
            destacado: false,
        });
        setErrores({});
        setImagenNombre(null);
        setSelectedFile(null);
        setImagePreview(null);
        setPreviousImagePath(null);
    };

    const validarFormulario = () => {
        const nuevoErrores = {};
        if (!formData.titulo.trim()) {
            nuevoErrores.titulo = "El titulo es obligatorio.";
        }
        if (!formData.contenido.trim()) {
            nuevoErrores.contenido = "El contenido es obligatorio.";
        }
        // categoria y areaNegocio tienen valores por defecto en el payload, no necesitan validación
        // El autor se llena automáticamente, no necesita validación
        /* if (!formData.imagen && !selectedFile) {
            nuevoErrores.imagen = "La imagen es obligatoria.";
        } */
        setErrores(nuevoErrores);
        return Object.keys(nuevoErrores).length === 0;
    };

    const recargarArticulos = async () => {
        const response = await axios.get(ENDPOINTS.articulos);
        const data = response.data;
        const areasMapReload = {};
        areasNegocio.forEach(a => { areasMapReload[a.idAreaNegocio ?? a.IdAreaNegocio] = a.nombre ?? a.Nombre; });
        const categoriasMapReload = {};
        categorias.forEach(c => { categoriasMapReload[c.idCategoria ?? c.IdCategoria] = c.nombre ?? c.Nombre; });
        const transformados = data.map(a => ({
            id: a.idArticulo ?? a.IdArticulo,
            titulo: a.titulo ?? a.Titulo,
            categoria: a.nombreCategoria ?? a.NombreCategoria ?? (categoriasMapReload[a.idCategoria ?? a.IdCategoria] || "General"),
            idCategoria: a.idCategoria ?? a.IdCategoria,
            area: a.nombreArea ?? a.NombreArea ?? (areasMapReload[a.idAreaNegocio ?? a.IdAreaNegocio] || "Corporativo"),
            idAreaNegocio: a.idAreaNegocio ?? a.IdAreaNegocio,
            contenido: a.contenido ?? a.Contenido,
            autor: a.nombreAutor ?? a.NombreAutor ?? "Actualizado",
            fechaPublicacion: (a.fechaPublicacion ?? a.FechaPublicacion) ? (a.fechaPublicacion ?? a.FechaPublicacion).slice(0, 10) : getTodayISO(),
            imagen: (a.imagenUrl ?? a.ImagenUrl) || null,
            resumen: (a.resumen ?? a.Resumen) || "",
            destacado: a.destacado ?? a.Destacado ?? false,
            esOrigen: a.esOrigen ?? a.EsOrigen ?? false
        }));
        transformados.sort((a, b) => {
            if (a.destacado && !b.destacado) return -1;
            if (!a.destacado && b.destacado) return 1;
            const dateA = new Date(a.fechaPublicacion || 0).getTime();
            const dateB = new Date(b.fechaPublicacion || 0).getTime();
            if (dateB !== dateA) return dateB - dateA;
            return b.id - a.id;
        });
        setArticulos(transformados);
    };

    const executePublish = async (payload, selectedShareCompanies = {}) => {
        try {
            setSharingLoading(true);
            setUploading(true);

            let currentArticuloId = selectedId;
            if (selectedId) {
                payload.idArticulo = selectedId;
                await axios.put(ENDPOINTS.articulos, payload);
            } else {
                const resPost = await axios.post(ENDPOINTS.articulos, payload);
                currentArticuloId = resPost.data;
            }

            // Compartir con empresas descendientes
            if (currentArticuloId) {
                try {
                    const resShared = await axios.get(ENDPOINTS.articulosCompartidosByArticulo(currentArticuloId));
                    const existingShared = resShared.data || [];

                    for (const idEmpStr of Object.keys(selectedShareCompanies)) {
                        const empId = Number(idEmpStr);
                        const isSelected = selectedShareCompanies[empId];
                        const existingRec = existingShared.find(s => (s.idEmpresa || s.IdEmpresa) === empId);

                        if (isSelected && !existingRec) {
                            await axios.post(ENDPOINTS.articulosCompartidos, {
                                idArticulo: currentArticuloId,
                                idEmpresa: empId,
                                activo: true
                            });
                        } else if (!isSelected && existingRec) {
                            const recId = existingRec.idArticuloCompartido || existingRec.IdArticuloCompartido;
                            await axios.delete(`${ENDPOINTS.articulosCompartidos}/${recId}`);
                        }
                    }
                } catch (shareErr) {
                    console.error("Error actualizando compartición", shareErr);
                }
            }

            await recargarArticulos();
            setShowModal(false);
            setShowShareModal(false);
            limpiarFormulario();
            setAlertDialog({ open: true, title: "Éxito", message: selectedId ? "Artículo actualizado correctamente." : "Artículo creado correctamente.", variant: "success" });
        } catch (error) {
            console.error("Error al guardar:", error);
            setAlertDialog({ open: true, title: "Error", message: "No se pudo guardar el artículo.", variant: "error" });
        } finally {
            setUploading(false);
            setSharingLoading(false);
        }
    };

    const handleGuardar = async (e) => {
        e.preventDefault();
        if (!validarFormulario()) return;

        try {
            setUploading(true);
            const idUsuario = getSessionUserId();

            // 1. Subir imagen si existe nueva
            let finalImageUrl = formData.imagen;

            if (selectedFile) {
                if (selectedId && previousImagePath) {
                    try {
                        await axios.delete(UPLOAD_ENDPOINT, { params: { filePath: previousImagePath } });
                    } catch (err) {
                        console.warn("No se pudo eliminar la imagen anterior:", err);
                    }
                }
                const formDataImg = new FormData();
                formDataImg.append('file', selectedFile);
                const resImg = await axios.post(UPLOAD_ENDPOINT, formDataImg, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                finalImageUrl = resImg.data.filePath;
                setPreviousImagePath(finalImageUrl);
            }

            const payload = {
                idArticulo: selectedId || 0,
                titulo: formData.titulo,
                resumen: formData.resumen || formData.contenido.substring(0, 200),
                contenido: formData.contenido,
                idAutor: idUsuario,
                idCategoria: parseInt(formData.categoria) || (categorias.length > 0 ? (categorias[0].idCategoria ?? categorias[0].IdCategoria) : 1),
                idAreaNegocio: parseInt(formData.areaNegocio) || (areasNegocio.length > 0 ? (areasNegocio[0].idAreaNegocio ?? areasNegocio[0].IdAreaNegocio) : 1),
                imagenUrl: finalImageUrl || "",
                destacado: formData.destacado || false,
                fechaPublicacion: formData.fechaPublicacion || getTodayISO(),
                fechaCreacion: new Date().toISOString(),
                idEmpresa: getSessionEmpresaId(),
                activo: true
            };

            // Obtener empresas descendientes
            const sessionEmpresaId = getSessionEmpresaId();
            const resDesc = await axios.get(ENDPOINTS.empresasDescendientes(sessionEmpresaId));
            const descendientes = resDesc.data || [];

            // Si no hay descendientes, publicar directo
            if (descendientes.length === 0) {
                await executePublish(payload, {});
                return;
            }

            // Si hay descendientes, precargar selección existente (para edición)
            let preSelected = {};
            if (selectedId) {
                try {
                    const resShared = await axios.get(ENDPOINTS.articulosCompartidosByArticulo(selectedId));
                    const sharedData = resShared.data || [];
                    sharedData.forEach(sh => {
                        if (!sh.esOrigen && !sh.EsOrigen) {
                            const empId = sh.IdEmpresa || sh.idEmpresa;
                            preSelected[empId] = true;
                        }
                    });
                } catch (e) { console.warn("Could not fetch shared", e); }
            }

            setPendingPayload(payload);
            setPendingFinalImageUrl(finalImageUrl);
            setDescendantCompanies(descendientes);
            setSelectedCompanies(preSelected);
            setShowModal(false);
            setShowShareModal(true);
        } catch (error) {
            console.error("Error al preparar publicación:", error);
            setAlertDialog({ open: true, title: "Error", message: "No se pudo verificar las empresas disponibles.", variant: "error" });
        } finally {
            setUploading(false);
        }
    };

    const handleToggleDestacado = async (articulo) => {
        try {
            const idUsuario = getSessionUserId();
            const payload = {
                idArticulo: articulo.id,
                titulo: articulo.titulo,
                resumen: articulo.resumen || articulo.contenido.substring(0, 200),
                contenido: articulo.contenido,
                idAutor: idUsuario,
                idCategoria: articulo.idCategoria,
                idAreaNegocio: articulo.idAreaNegocio,
                imagenUrl: articulo.imagen || "",
                destacado: !articulo.destacado,
                fechaPublicacion: articulo.fechaPublicacion,
                fechaCreacion: new Date().toISOString(),
                activo: true
            };

            await axios.put(ENDPOINTS.articulos, payload);

            // Actualizar estado local
            setArticulos(prev => {
                const updated = prev.map(a =>
                    a.id === articulo.id ? { ...a, destacado: !a.destacado } : a
                );
                // Re-ordenar
                return updated.sort((a, b) => {
                    if (a.destacado && !b.destacado) return -1;
                    if (!a.destacado && b.destacado) return 1;
                    const dateA = new Date(a.fechaPublicacion || 0).getTime();
                    const dateB = new Date(b.fechaPublicacion || 0).getTime();
                    if (dateB !== dateA) return dateB - dateA;
                    return b.id - a.id;
                });
            });
        } catch (error) {
            console.error("Error al actualizar destacado:", error);
            setAlertDialog({ open: true, title: "Error", message: "No se pudo actualizar el estado, inténtalo de nuevo.", variant: "error" });
        }
    };

    const handleSeleccionarArticulo = (articulo) => {
        setSelectedId(articulo.id);
        setFormData({
            titulo: articulo.titulo,
            resumen: articulo.resumen || "",
            categoria: articulo.idCategoria,
            areaNegocio: articulo.idAreaNegocio,
            contenido: articulo.contenido,
            autor: articulo.autor,
            fechaPublicacion: articulo.fechaPublicacion,
            imagen: articulo.imagen || null,
            destacado: articulo.destacado || false,
        });
        setImagePreview(articulo.imagen ? (articulo.imagen.startsWith('http') ? articulo.imagen : `${SERVER_URL}${articulo.imagen}`) : null);
        setSelectedFile(null);
        setPreviousImagePath(articulo.imagen || null);
        setImagenNombre(articulo.imagen ? articulo.imagen.split('/').pop() : null);
        setErrores({});

        // Abrir modal en modo edición
        setModalMode('edit');
        setShowModal(true);
    };

    const executeDelete = async () => {
        const articuloToDelete = deleteDialog.item;
        if (!articuloToDelete) return;

        try {
            // Borrar imagen de S3/Local si existe
            if (articuloToDelete.imagen) {
                try {
                    await axios.delete(UPLOAD_ENDPOINT, {
                        params: { filePath: articuloToDelete.imagen }
                    });
                } catch (imgErr) {
                    console.warn("No se pudo eliminar la imagen física:", imgErr);
                }
            }

            await axios.delete(`${ENDPOINTS.articulos}/${articuloToDelete.id}`);
            setArticulos((prev) => prev.filter((a) => a.id !== articuloToDelete.id));
            limpiarFormulario();
            setDeleteDialog({ open: false, item: null });
            setAlertDialog({ open: true, title: "Eliminado", message: "El artículo ha sido eliminado.", variant: "success" });
        } catch (error) {
            console.error(error);
        }
    };

    const handleEliminar = () => {
        if (!selectedId) return;
        const articulo = articulos.find(a => a.id === selectedId);
        setDeleteDialog({ open: true, item: articulo || { titulo: "este artículo", id: selectedId } });
    };

    const articulosFiltrados = useMemo(() => {
        return articulos.filter((a) => {
            if (
                filtros.texto &&
                !(
                    a.titulo.toLowerCase().includes(filtros.texto.toLowerCase()) ||
                    a.contenido.toLowerCase().includes(filtros.texto.toLowerCase()) ||
                    (a.autor || "").toLowerCase().includes(filtros.texto.toLowerCase())
                )
            ) {
                return false;
            }
            if (filtros.categoria !== "Todas" && a.categoria !== filtros.categoria) {
                return false;
            }
            if (filtros.area && filtros.area !== "Todas" && a.area !== filtros.area) { // Nuevo filtro Área
                return false;
            }
            return true;
        });
    }, [articulos, filtros]);

    return (
        <div className="intra-news-page">
            {/* HEADER */}
            <header className="intra-news-header">
                <div>
                    {onNavigate && (
                        <button
                            type="button"
                            className="intra-button-ghost btn-cancel"
                            onClick={() => onNavigate("dashboard")}
                            style={{
                                marginBottom: '0.75rem'
                            }}
                        >
                            <span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>←</span>
                            <span>Volver al dashboard</span>
                        </button>
                    )}
                    <h1 className="intra-news-title">Artículos</h1>
                </div>
            </header>

            {/* FILTROS */}
            <section className="intra-news-toolbar">
                <div className="intra-news-filters" style={{ width: '100%', display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                    <div className="intra-filter-group" style={{ flex: '1', minWidth: '200px' }}>
                        <label className="intra-label" htmlFor="filtroTexto">
                            Buscar
                        </label>
                        <input
                            id="filtroTexto"
                            name="texto"
                            type="text"
                            className="intra-input"
                            value={filtros.texto}
                            onChange={handleFiltrosChange}
                            placeholder="Buscar artículos..."
                        />
                    </div>

                    <div className="intra-filter-group" style={{ width: '180px' }}>
                        <label className="intra-label" htmlFor="filtroArea">
                            Área
                        </label>
                        <select
                            id="filtroArea"
                            name="area"
                            className="intra-select"
                            value={filtros.area || "Todas"}
                            onChange={handleFiltrosChange}
                        >
                            <option key="all-areas" value="Todas">Todas</option>
                            {areasNegocio.map((areaItem, idx) => (
                                <option key={areaItem.idAreaNegocio || areaItem.IdAreaNegocio || idx} value={areaItem.nombre || areaItem.Nombre}>
                                    {areaItem.nombre || areaItem.Nombre}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="intra-filter-group" style={{ width: '180px' }}>
                        <label className="intra-label" htmlFor="filtroCategoria">
                            Categoría
                        </label>
                        <select
                            id="filtroCategoria"
                            name="categoria"
                            className="intra-select"
                            value={filtros.categoria}
                            onChange={handleFiltrosChange}
                        >
                            <option key="all-cats" value="Todas">Todas</option>
                            {categorias.map((cat, idx) => (
                                <option key={cat.idCategoria || cat.IdCategoria || idx} value={cat.nombre || cat.Nombre}>
                                    {cat.nombre || cat.Nombre}
                                </option>
                            ))}
                        </select>
                    </div>

                    <button
                        type="button"
                        className="btn-action"
                        onClick={() => { limpiarFormulario(); setModalMode('create'); setShowModal(true); }}
                        style={{
                            alignSelf: 'flex-end',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        + Nuevo artículo
                    </button>
                </div>
            </section>

            {/* LAYOUT PRINCIPAL - LISTA COMPLETA */}
            <section className="intra-news-layout" style={{ display: 'block' }}>
                <div className="intra-news-list-panel" style={{ width: '100%' }}>
                    <div className="intra-panel-header">
                        <h2 className="intra-panel-title">Artículos publicados</h2>
                        <p className="intra-news-count">
                            {articulosFiltrados.length} artículo{articulosFiltrados.length === 1 ? "" : "s"} encontrado{articulosFiltrados.length === 1 ? "" : "s"}
                        </p>
                    </div>

                    {articulosFiltrados.length === 0 ?
                        <p className="intra-avisos-empty">
                            No se encontraron artículos con los filtros seleccionados.
                        </p>
                        : (
                            <div className="intra-avisos-table-wrapper">
                                <table className="intra-avisos-table" style={{ borderCollapse: 'separate', borderSpacing: '0 10px' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '80px', textAlign: 'center' }}>FAVORITO</th>
                                            <th>IMAGEN</th>
                                            <th>TÍTULO</th>
                                            <th>Área de Negocio</th>
                                            <th>Categoría</th>
                                            <th>AUTOR</th>
                                            <th>FECHA</th>
                                            <th>EDITAR</th>
                                            <th>ELIMINAR</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {articulosFiltrados.map((articulo) => {
                                            const isDest = articulo.destacado;
                                            const borderColor = '#fbbf24';
                                            const borderStyle = isDest ? `2px solid ${borderColor}` : '2px solid transparent';
                                            const bgColor = isDest ? '#fffbeb' : 'transparent';

                                            return (
                                                <tr key={articulo.id} style={{ backgroundColor: bgColor }}>
                                                    <td style={{ textAlign: 'center', borderTop: borderStyle, borderBottom: borderStyle, borderLeft: borderStyle, borderTopLeftRadius: isDest ? '12px' : '0', borderBottomLeftRadius: isDest ? '12px' : '0' }}>
                                                        <span
                                                            onClick={(e) => { e.stopPropagation(); handleToggleDestacado(articulo); }}
                                                            style={{
                                                                cursor: 'pointer',
                                                                color: articulo.destacado ? '#fbbf24' : '#e2e8f0',
                                                                fontSize: '2rem',
                                                                transition: 'color 0.2s ease'
                                                            }}
                                                            title={articulo.destacado ? "Quitar favorito" : "Marcar como favorito"}
                                                        >
                                                            ★
                                                        </span>
                                                    </td>
                                                    <td style={{ width: '80px', padding: '0.5rem', borderTop: borderStyle, borderBottom: borderStyle }}>
                                                        {articulo.imagen ? (
                                                            <img
                                                                src={articulo.imagen.startsWith('http') ? articulo.imagen : `${SERVER_URL}${articulo.imagen}`}
                                                                alt={articulo.titulo}
                                                                style={{
                                                                    width: '60px',
                                                                    height: '60px',
                                                                    objectFit: 'cover',
                                                                    borderRadius: '6px',
                                                                    border: '1px solid #e2e8f0'
                                                                }}
                                                            />
                                                        ) : (
                                                            <div style={{
                                                                width: '60px',
                                                                height: '60px',
                                                                backgroundColor: '#f1f5f9',
                                                                borderRadius: '6px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: '#cbd5e1',
                                                                fontSize: '1.5rem'
                                                            }}>
                                                                🖼
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ borderTop: borderStyle, borderBottom: borderStyle }}>
                                                        <div className="intra-avisos-title-main">{articulo.titulo}</div>
                                                        <div className="intra-avisos-title-sub">{articulo.contenido.substring(0, 50)}...</div>
                                                        <div style={{ minHeight: '1.2rem', marginTop: '0.2rem' }}>
                                                            <span style={{ fontSize: '0.7rem', backgroundColor: '#fbbf24', padding: '0.1rem 0.4rem', borderRadius: '4px', display: 'inline-block', visibility: articulo.destacado ? 'visible' : 'hidden', opacity: articulo.destacado ? 1 : 0, transition: 'opacity 0.2s' }}>Destacado</span>
                                                        </div>
                                                    </td>
                                                    <td style={{ borderTop: borderStyle, borderBottom: borderStyle }}>
                                                        <span className="intra-avisos-meta">
                                                            {articulo.area}
                                                        </span>
                                                    </td>
                                                    <td style={{ borderTop: borderStyle, borderBottom: borderStyle }}>
                                                        <span className="intra-avisos-meta">
                                                            {articulo.categoria}
                                                        </span>
                                                    </td>
                                                    <td style={{ borderTop: borderStyle, borderBottom: borderStyle }}>{articulo.autor}</td>
                                                    <td style={{ borderTop: borderStyle, borderBottom: borderStyle }}>{articulo.fechaPublicacion}</td>
                                                    <td style={{ borderTop: borderStyle, borderBottom: borderStyle }}>
                                                        {(articulo.esOrigen || isSessionSuperAdmin()) && (
                                                            <button
                                                                type="button"
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                                                onClick={() => handleSeleccionarArticulo(articulo)}
                                                                title="Editar artículo"
                                                            >
                                                                <img src={sigmaEdit} alt="Editar" style={{ width: '40px', height: 'auto' }} />
                                                            </button>
                                                        )}
                                                    </td>
                                                    <td style={{ borderTop: borderStyle, borderBottom: borderStyle, borderRight: borderStyle, borderTopRightRadius: isDest ? '12px' : '0', borderBottomRightRadius: isDest ? '12px' : '0' }}>
                                                        {(articulo.esOrigen || isSessionSuperAdmin()) && (
                                                            <button
                                                                type="button"
                                                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedId(articulo.id);
                                                                    handleEliminar();
                                                                }}
                                                                title="Eliminar artículo"
                                                            >
                                                                <img src={sigmaDelete} alt="Eliminar" style={{ width: '40px', height: 'auto' }} />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                </div>
            </section>

            {/* MODAL */}
            {showModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }} onClick={() => setShowModal(false)}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        width: '90%',
                        maxWidth: '900px',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }} onClick={(e) => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div className="intra-panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.5rem 2rem', borderBottom: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setFormData(prev => ({ ...prev, destacado: !prev.destacado }));
                                        if (modalMode === 'edit' && selectedId) {
                                            const articuloActual = articulos.find(a => a.id === selectedId);
                                            if (articuloActual) {
                                                handleToggleDestacado(articuloActual);
                                            }
                                        }
                                    }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '2rem',
                                        cursor: 'pointer',
                                        padding: '0.25rem',
                                        lineHeight: 1,
                                        transition: 'color 0.2s ease, transform 0.1s',
                                        color: formData.destacado ? '#fbbf24' : '#e2e8f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                    onMouseDown={e => e.currentTarget.style.transform = 'scale(0.85)'}
                                    onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
                                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                                    title={formData.destacado ? "Quitar de favoritos" : "Marcar como favorito"}
                                >
                                    ★
                                </button>
                                <h2 className="intra-panel-title" style={{ margin: 0, fontSize: '1.25rem' }}>
                                    {modalMode === 'create' ? "Nuevo artículo" : "Detalle del artículo"}
                                </h2>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                {/* Save button removed from here */}
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '2rem',
                                        lineHeight: 1,
                                        color: '#64748b',
                                        cursor: 'pointer'
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        {/* Modal Content - Scrollable */}
                        <div style={{ padding: '2rem', overflowY: 'auto' }}>
                            <form className="intra-avisos-form" onSubmit={handleGuardar}>
                                <div className="intra-form-group">
                                    <label className="intra-label">Título</label>
                                    <input
                                        name="titulo"
                                        type="text"
                                        className={`intra-input ${errores.titulo ? "intra-input-error" : ""}`}
                                        value={formData.titulo}
                                        onChange={handleFormChange}
                                        placeholder="Ej. Optimización de procesos productivos"
                                    />
                                    {errores.titulo && <p className="intra-error-text">{errores.titulo}</p>}
                                </div>

                                <div className="intra-form-group">
                                    <label className="intra-label">Resumen / Extracto</label>
                                    <textarea
                                        name="resumen"
                                        className="intra-textarea"
                                        rows={2}
                                        value={formData.resumen}
                                        onChange={handleFormChange}
                                        placeholder="Breve descripción para el listado..."
                                    />
                                </div>

                                <div className="intra-form-group">
                                    <label className="intra-label">Contenido</label>
                                    <textarea
                                        name="contenido"
                                        className={`intra-textarea ${errores.contenido ? "intra-input-error" : ""}`}
                                        rows={10}
                                        value={formData.contenido}
                                        onChange={handleFormChange}
                                        placeholder="Escribe el contenido completo del artículo..."
                                    />
                                    {errores.contenido && <p className="intra-error-text">{errores.contenido}</p>}
                                </div>

                                <div className="intra-form-row">
                                    <div className="intra-form-group">
                                        <label className="intra-label">Categoría</label>
                                        <select
                                            name="categoria"
                                            className="intra-select"
                                            value={formData.categoria || ""}
                                            onChange={handleFormChange}
                                        >
                                            {categorias.map((cat, idx) => (
                                                <option key={cat.idCategoria || cat.IdCategoria || idx} value={cat.idCategoria || cat.IdCategoria}>
                                                    {cat.nombre || cat.Nombre}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="intra-form-group">
                                        <label className="intra-label">Área de Negocio</label>
                                        <select
                                            name="areaNegocio"
                                            className="intra-select"
                                            value={formData.areaNegocio || ""}
                                            onChange={handleFormChange}
                                        >
                                            {areasNegocio.map((areaItem, idx) => (
                                                <option key={areaItem.idAreaNegocio || areaItem.IdAreaNegocio || idx} value={areaItem.idAreaNegocio || areaItem.IdAreaNegocio}>
                                                    {areaItem.nombre || areaItem.Nombre}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="intra-form-group">
                                    <label className="intra-label">Imagen (opcional)</label>
                                    <div className={`file-uploader${imagePreview ? ' file-uploader--filled' : ''}${errores.imagen ? ' file-uploader--error' : ''}`}>
                                        {imagePreview && (
                                            <div className="file-uploader__badge">
                                                <span>✓</span>
                                                <span>Archivo cargado</span>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault(); e.stopPropagation();
                                                        setImagePreview(null); setSelectedFile(null); setFormData(p => ({ ...p, imagen: null }));
                                                    }}
                                                    style={{ marginLeft: '8px', background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem' }}
                                                    title="Eliminar imagen"
                                                >×</button>
                                            </div>
                                        )}
                                        {imagePreview && (
                                            <div className="file-uploader__preview">
                                                <img
                                                    src={imagePreview}
                                                    alt="Vista previa"
                                                    style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '6px', objectFit: 'contain' }}
                                                />
                                            </div>
                                        )}
                                        <label className="file-uploader__label">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onClick={(e) => { e.target.value = ''; }}
                                                onChange={handleFileChange}
                                            />
                                            <span className="file-uploader__icon">{imagePreview ? '↻' : '☁'}</span>
                                            <span className="file-uploader__text">{imagePreview ? 'Cambiar imagen' : 'Haz clic para subir imagen'}</span>
                                            <span className="file-uploader__hint">JPG, PNG, WEBP (Máx. {getSystemConfig('LIMITE_PESO_IMAGENES', '5')}MB)</span>
                                        </label>
                                    </div>
                                    {errores.imagen && <p className="intra-error-text" style={{ marginTop: '0.5rem' }}>{errores.imagen}</p>}
                                </div>
                                <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', paddingTop: '1rem', borderTop: '1px solid #f1f5f9' }}>
                                    <button
                                        type="submit"
                                        className="btn-action"
                                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: uploading ? 0.7 : 1, cursor: uploading ? 'not-allowed' : 'pointer' }}
                                        disabled={uploading}
                                    >
                                        <span>{uploading ? 'Guardando...' : 'Guardar cambios'}</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div >
                </div >
            )}

            {/* MODAL COMPARTIR ARTÍCULO */}
            {showShareModal && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1050, padding: '2rem' }}>
                <div style={{ width: '100%', maxWidth: '500px', backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)' }}>
                  <div style={{ padding: '1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc' }}>
                    <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#1e293b' }}>Compartir Artículo</h2>
                    <button type="button" onClick={() => { setShowShareModal(false); setShowModal(true); }} style={{ background: 'none', border: 'none', fontSize: '2rem', color: '#64748b', cursor: 'pointer', lineHeight: 1 }}>×</button>
                  </div>
                  <div style={{ padding: '1.5rem', overflowY: 'auto', maxHeight: '60vh' }}>
                    <p style={{ marginBottom: '1.5rem', color: '#475569' }}>Seleccione las empresas con las que desea compartir este artículo:</p>
                    {descendantCompanies.length === 0 ? (
                      <p style={{ color: '#94a3b8', fontStyle: 'italic', textAlign: 'center' }}>No hay empresas hijas disponibles.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {descendantCompanies.map(comp => {
                          const cId = comp.IdEmpresa || comp.idEmpresa;
                          const cName = comp.Nombre || comp.nombre;
                          const cParent = comp.NombrePadre || comp.nombrePadre || '';
                          return (
                            <div key={cId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', backgroundColor: '#f1f5f9', borderRadius: '8px' }}>
                              <div>
                                <div style={{ fontWeight: 'bold', color: '#1e293b' }}>{cName}</div>
                                <div style={{ fontSize: '0.85rem', color: '#64748b' }}>({cParent})</div>
                              </div>
                              <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px', flexShrink: 0 }}>
                                <input
                                  type="checkbox"
                                  checked={!!selectedCompanies[cId]}
                                  onChange={(ev) => setSelectedCompanies(prev => ({ ...prev, [cId]: ev.target.checked }))}
                                  style={{ opacity: 0, width: 0, height: 0 }}
                                />
                                <span style={{ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: selectedCompanies[cId] ? '#10b981' : '#cbd5e1', transition: '.4s', borderRadius: '24px' }}>
                                  <span style={{ position: 'absolute', height: '18px', width: '18px', left: '3px', bottom: '3px', backgroundColor: 'white', transition: '.4s', borderRadius: '50%', transform: selectedCompanies[cId] ? 'translateX(20px)' : 'translateX(0)' }} />
                                </span>
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div style={{ padding: '1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                    <button type="button" onClick={() => { setShowShareModal(false); setShowModal(true); }} className="intra-button-ghost">Cancelar</button>
                    <button
                      type="button"
                      onClick={() => executePublish(pendingPayload, selectedCompanies)}
                      className="btn-action"
                      disabled={sharingLoading}
                      style={{ opacity: sharingLoading ? 0.7 : 1 }}
                    >
                      {sharingLoading ? 'Guardando...' : 'Confirmar y Guardar'}
                    </button>
                  </div>
                </div>
              </div>
            )}
            <ConfirmDeleteDialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, item: null })}
                onConfirm={executeDelete}
                roleName={deleteDialog.item?.titulo || "este artículo"}
            />
            <AlertDialog
                open={alertDialog.open}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
                onClose={() => setAlertDialog({ open: false, title: "", message: "", variant: "info" })}
            />
        </div >
    );
};

export default Articulos;
